import { MAX_COLS } from "@components/layout/MasonryList";
import { css } from "@emotion/react";
import React, { useState, useMemo, useEffect } from "react";

import { TagResponse } from "@every.org/common/src/codecs/entities";
import { CAUSE_ILLUSTRATION_BG_PALETTE } from "@every.org/common/src/display/palette";
import {
  SearchQueryParam,
  FeedItemType,
} from "@every.org/common/src/entity/types";
import {
  clientRouteMetas,
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { constructCloudinaryUrl } from "@every.org/common/src/helpers/cloudinary";
import { removeUndefinedOrNullValues } from "@every.org/common/src/helpers/objectUtilities";

import { avatarCssBySize, AvatarSize } from "src/components/Avatar";
import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "src/components/Button";
import { Icon, IconDisplay, IconSize } from "src/components/Icon";
import { DefaultTabNavigation, TabHeader } from "src/components/TabHeader";
import { TagIllustrationOld } from "src/components/TagIllustration";
import { TagGiftsTab } from "src/components/Tags/TagGiftsTab";
import { TagHeaderCard } from "src/components/Tags/TagHeaderCard";
import {
  FeedItems,
  TagNonprofitsTab,
  TagTabProps,
} from "src/components/Tags/TagNonprofitsTab";
import {
  buildOgDescription,
  getTagShareCloudinaryParams,
} from "src/components/Tags/helpers";
import { DefaultPageLayout } from "src/components/layout/DefaultPageLayout";
import {
  headerCardPageSectionCss,
  PageSection,
} from "src/components/layout/PageSection";
import { FILTER_MODAL_URL_PARAM } from "src/constants/searchFilter";
import { LoadImageWithPriorityProvider } from "src/context/LoadImageWithPriorityContext/LoadImageWithPriorityContext";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { NotFoundPage } from "src/pages/NotFound";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { OPENGRAPH_DIMENSIONS } from "src/utility/opengraph";
import { NONPROFIT_PAGE_HEADER_OVERLAP_HEIGHT } from "src/utility/pageMetadata";

enum TagPageTab {
  NONPROFITS = "Nonprofits",
  DONATIONS = "Donations",
}

const UNEXPECTED_MATCHED_ROUTE_TAG_MESSAGE =
  "Unexpected: matched route for tag page";

const TAG_ROUTE_NAME_FOR_TAB: {
  [tab in TagPageTab]: ClientRouteName;
} = {
  [TagPageTab.NONPROFITS]: ClientRouteName.FUND_OR_TAG_NONPROFITS,
  [TagPageTab.DONATIONS]: ClientRouteName.FUND_OR_TAG_GIFTS,
};

function determineSelectedTab(path: string) {
  // It's possible that a cause slug with be rendered by the nonprofit page
  // in that case the path will be incorrect so we adjust it for this switch
  switch (path.replace("/:nonprofitSlug", "/cause/:tagName")) {
    case clientRouteMetas[ClientRouteName.CAUSE].path:
    case clientRouteMetas[ClientRouteName.CAUSE_NONPROFITS].path:
      return TagPageTab.NONPROFITS;
    case clientRouteMetas[ClientRouteName.CAUSE_GIFTS].path:
      return TagPageTab.DONATIONS;
    default:
      throw new Error(UNEXPECTED_MATCHED_ROUTE_TAG_MESSAGE);
  }
}

const TagPageNav: React.FCC<{
  selectedTab: TagPageTab;
  tagName: string;
}> = ({ selectedTab, tagName }) => {
  const { push } = useEdoRouter();

  return (
    <div
      css={[
        horizontalStackCss.m,
        css`
          justify-content: space-between;
          margin: 0px ${spacing.m};
          position: relative;
        `,
      ]}
    >
      <DefaultTabNavigation role="navigation" aria-label="Tag page tabs">
        {Object.values(TagPageTab).map((currentTab) => (
          <li key={currentTab}>
            <TabHeader
              currentTab={currentTab}
              selectedTab={selectedTab}
              to={getRoutePath({
                name: TAG_ROUTE_NAME_FOR_TAB[currentTab],
                format: URLFormat.RELATIVE,
                tokens: { nonprofitSlug: tagName },
              })}
              data-tname={`${currentTab}_tab`}
            >
              {currentTab}
            </TabHeader>
          </li>
        ))}
      </DefaultTabNavigation>
      <Button
        data-tname="filterCauseButton"
        role={ButtonRole.TEXT_ONLY}
        size={ButtonSize.SMALL}
        contentCss={[horizontalStackCss.xxs, { alignItems: "center" }]}
        css={{ position: "absolute", right: 0 }}
        onClick={{
          kind: ButtonTargetKind.FUNCTION,
          action: () => {
            push(
              getRoutePath({
                format: URLFormat.RELATIVE,
                name: ClientRouteName.SEARCH_RESULTS,
                query: {
                  [SearchQueryParam.CAUSES]: tagName,
                  [FILTER_MODAL_URL_PARAM]: "true",
                },
              })
            );
          },
        }}
      >
        <Icon
          iconImport={() => import("src/components/Icon/icons/FilterIcon")}
          display={IconDisplay.ACCENT}
          size={IconSize.SMALL}
        />
        <span>Filter</span>
      </Button>
    </div>
  );
};

const TagPageContent: React.FCC<
  TagTabProps & {
    selectedTab: TagPageTab;
  }
> = ({ selectedTab, ...tagTabProps }) => {
  switch (selectedTab) {
    case TagPageTab.NONPROFITS:
      return <TagNonprofitsTab {...tagTabProps} />;
    case TagPageTab.DONATIONS:
      return <TagGiftsTab {...tagTabProps} />;
  }
};

/**
 * The tag page uses the cause page as a template to recommend nonprofits
 * related to the tag
 */
const TagFeedPage: React.FCC<{
  tag: TagResponse;
  initialFeedItems?: FeedItems;
}> = ({ tag, initialFeedItems }) => {
  const [hideFooter, setHideFooter] = useState<boolean>(true);
  const { getPathnameWithParams } = useEdoRouter();
  const { path } = getPathnameWithParams();

  const [selectedTab, setSelectedTab] = useState<TagPageTab | undefined>(
    TagPageTab.NONPROFITS
  );

  const {
    tagName,
    tagImageCloudinaryId,
    title,
    shareText,
    topNonprofits,
    causeCategory,
  } = tag;

  const ogDescription = shareText || buildOgDescription(title, topNonprofits);
  const shareImageCloudinaryParams = useMemo(
    () => getTagShareCloudinaryParams(tag),
    [tag]
  );

  useEffect(() => {
    try {
      setSelectedTab(determineSelectedTab(path));
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === UNEXPECTED_MATCHED_ROUTE_TAG_MESSAGE) {
          setSelectedTab(undefined);
        }
        throw error;
      }
    }
  }, [path]);

  if (!selectedTab) {
    return <NotFoundPage />;
  }

  const headerContent = (
    <div
      css={css`
        display: flex;
        justify-content: center;
      `}
    >
      <TagIllustrationOld
        tagImageCloudinaryId={tagImageCloudinaryId}
        tagName={tagName}
        size={AvatarSize.X_LARGE}
        causeCategory={tag.causeCategory}
        customCSS={cssForMediaSize({
          min: MediaSize.LARGE,
          css: avatarCssBySize[AvatarSize.XXX_LARGE],
        })}
      />
    </div>
  );

  const feedItemType = path.includes("donations")
    ? FeedItemType.USER_DONATION
    : FeedItemType.NONPROFIT_RECOMMENDATION;

  const itemsIdWithPriority = initialFeedItems
    ? initialFeedItems
        ?.filter((item) => item.type === feedItemType)
        .slice(0, Math.min(initialFeedItems.length, MAX_COLS))
        // If again to narrow the type
        .map((item) =>
          item.type === FeedItemType.NONPROFIT_RECOMMENDATION
            ? item.nonprofitId
            : item.type === FeedItemType.USER_DONATION
            ? item.donationCharge.id
            : ""
        )
    : [];

  return (
    <DefaultPageLayout
      pageTitle={`Support ${title}`}
      philanthropyforEveryoneSuffix
      metas={removeUndefinedOrNullValues({
        "og:description": ogDescription,
        "og:image":
          shareImageCloudinaryParams &&
          constructCloudinaryUrl({
            ...shareImageCloudinaryParams,
            dimensions: OPENGRAPH_DIMENSIONS,
          }),
      })}
      headerCss={{
        backgroundColor: CAUSE_ILLUSTRATION_BG_PALETTE[causeCategory],
      }}
      headerOverlapHeight={NONPROFIT_PAGE_HEADER_OVERLAP_HEIGHT}
      headerContent={headerContent}
      pageContentCss={verticalStackCss.xl}
      hideFooter={hideFooter}
    >
      <PageSection contentCss={headerCardPageSectionCss}>
        <TagHeaderCard tag={tag} />
      </PageSection>
      <PageSection contentCss={verticalStackCss.xl}>
        <TagPageNav selectedTab={selectedTab} tagName={tagName} />
        <LoadImageWithPriorityProvider
          initialValue={{ ids: itemsIdWithPriority }}
        >
          <TagPageContent
            initialFeedItems={initialFeedItems}
            selectedTab={selectedTab}
            tagName={tagName}
            onNoMoreItems={() => {
              setHideFooter(false);
            }}
          />
        </LoadImageWithPriorityProvider>
      </PageSection>
    </DefaultPageLayout>
  );
};

export default TagFeedPage;
